import type {ReactNode} from 'react';
import React, {useMemo} from 'react';
import type {GridProps} from '@mui/material';
import {Box, Grid} from '@mui/material';
import type {TextProps} from 'components/text/types';
import {ColorPalette} from 'theme';
import {IconButton} from 'components/button';
import {FlexBox} from 'components/layout';
import {Text} from 'components/text';
import type {TooltipProps} from 'components/tooltip';
import {Tooltip} from 'components/tooltip';
import {useSceneSizeContext} from './context/SceneSizeContext';

type Props = GridProps & {
  title?: string;
  tooltipTitle?: TooltipProps['title'];
  textProps?: TextProps;
  children?: ReactNode;
};

export const SceneTitle: React.FC<Props> = ({
  title,
  tooltipTitle,
  children,
  textProps,
  ...gridProps
}) => {
  const {fullWidth, toggleFullWidth} = useSceneSizeContext();

  const titleComponent = useMemo(() => {
    return title ? (
      <Box
        component={Grid}
        container
        sx={{width: '100%'}}
        flexDirection={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}>
        <Grid
          {...gridProps}
          sx={{
            overflow: 'hidden',
          }}>
          <FlexBox left center horizontal>
            {toggleFullWidth ? (
              <IconButton
                icon={fullWidth ? 'ChevronRight' : 'ChevronLeft'}
                onClick={toggleFullWidth}
              />
            ) : null}
            <Text
              data-cy={'scene-title'}
              sx={
                textProps?.sx
                  ? {color: ColorPalette.textPrimary, ...textProps.sx}
                  : {color: ColorPalette.textPrimary}
              }
              component={textProps?.component}
              variant={textProps?.variant ?? 'h1'}
              noWrap>
              {title}
            </Text>
            {tooltipTitle ? (
              <FlexBox>
                <Tooltip title={tooltipTitle} />
              </FlexBox>
            ) : null}
          </FlexBox>
        </Grid>
        {children}
      </Box>
    ) : null;
  }, [
    title,
    gridProps,
    toggleFullWidth,
    fullWidth,
    textProps,
    tooltipTitle,
    children,
  ]);

  return (
    <Box
      component={Grid}
      container
      sx={{width: '100%'}}
      flexDirection={'row'}
      justifyContent={'space-between'}
      alignItems={'center'}>
      <Grid size={{xs: 12}}>{titleComponent}</Grid>
    </Box>
  );
};
